import React, { useMemo, useRef, useState } from 'react'
import { Button, useTheme } from '@mui/material'
import Text from '../../Text'
import TextFieldCustom from '../../TextFieldCustom'
import Pilula from './Pilula'
import { parsearNumeros, parsearCSV } from '../../../libs/scheduledMessages'
import { acrescentarDestinatarios } from '../../../libs/destinatarios'

// Lista de destinatários de um envio em massa: importar planilha, colar
// números, conferir quem está na lista, renomear e remover. Usada pelo
// agendamento e pelo disparo em massa.
//
// A lista é ESTADO de quem usa (lista/onChange), não algo derivado do CSV ou do
// texto colado: sem isso os destinatários já salvos ficavam invisíveis na
// edição. `parsear` lê o texto colado (o agendamento usa a regra brasileira; o
// disparo, a do país escolhido e grupos). `antesDoTexto` entra ao lado do campo
// de colar (o seletor de país do disparo). `onColunas` recebe as colunas da
// planilha, que viram variáveis.
export default function ListaDeDestinatarios({
  lista,
  onChange,
  onColunas,
  onErro,
  parsear = parsearNumeros,
  antesDoTexto = null,
  placeholderColar = 'Um por linha. Para personalizar:\n5511999998888, Marcos Andrade\n5511977776666, Juliana'
}) {
  const theme = useTheme()
  const inputArquivo = useRef(null)
  const [modo, setModo] = useState('csv')
  const [textoNumeros, setTextoNumeros] = useState('')
  const [nomeArquivo, setNomeArquivo] = useState('')
  const [aviso, setAviso] = useState('')

  // Prévia do que o texto colado produziria — contador e inválidos ANTES de
  // o cliente clicar em adicionar.
  const previaColados = useMemo(() => parsear(textoNumeros), [parsear, textoNumeros])

  const erro = (msg) => { if (onErro) onErro(msg) }

  const lerArquivo = (evento) => {
    const arquivo = evento.target.files?.[0]
    if (!arquivo) return
    setNomeArquivo(arquivo.name)
    const leitor = new FileReader()
    leitor.onload = () => {
      const resultado = parsearCSV(String(leitor.result || ''))
      if (resultado.erro) {
        erro(resultado.erro)
        if (onColunas) onColunas([])
        return
      }
      erro('')
      if (onColunas) onColunas(resultado.variaveis || [])
      onChange(acrescentarDestinatarios(lista, resultado.validos))
      setAviso(
        `${resultado.validos.length} da planilha adicionados` +
        (resultado.invalidos.length ? ` · ${resultado.invalidos.length} linha(s) ignorada(s)` : '')
      )
    }
    leitor.readAsText(arquivo)
  }

  const adicionarColados = () => {
    if (!previaColados.validos.length) {
      erro('Nenhum número válido no texto colado')
      return
    }
    erro('')
    onChange(acrescentarDestinatarios(lista, previaColados.validos))
    setAviso(
      `${previaColados.validos.length} adicionado(s)` +
      (previaColados.invalidos.length ? ` · ${previaColados.invalidos.length} ignorado(s)` : '')
    )
    setTextoNumeros('')
  }

  const remover = (phone) => onChange(lista.filter((d) => d.phone !== phone))
  const renomear = (phone, nome) => onChange(lista.map((d) => (d.phone === phone ? { ...d, name: nome } : d)))

  return (
    <>
      <div className="mb-3">
        <Pilula ativo={modo === 'csv'} onClick={() => setModo('csv')}>Importar planilha</Pilula>
        <Pilula ativo={modo === 'colar'} onClick={() => setModo('colar')}>Colar números</Pilula>
      </div>

      {modo === 'csv' ? (
        <div>
          <input ref={inputArquivo} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={lerArquivo} />
          <Button
            size="small"
            variant="outlined"
            onClick={() => inputArquivo.current?.click()}
            sx={{ textTransform: 'none', borderRadius: '9px' }}
          >
            {nomeArquivo || 'Escolher arquivo CSV'}
          </Button>
          <Text className="font-light text-xs mt-2">
            A planilha precisa de uma coluna <b>telefone</b>. Uma coluna <b>nome</b>
            preenche a variável {'{{nome}}'}; as demais viram variáveis próprias.
          </Text>
        </div>
      ) : (
        <div>
          <div className="flex items-start gap-2">
            {antesDoTexto}
            <div style={{ flex: 1, minWidth: 0 }}>
              <TextFieldCustom
                label="Números"
                multiline
                rows={4}
                value={textoNumeros}
                onChange={(e) => setTextoNumeros(e.target.value)}
                placeholder={placeholderColar}
              />
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-2 mt-2">
            <Button
              size="small"
              variant="outlined"
              onClick={adicionarColados}
              disabled={!previaColados.validos.length}
              sx={{ textTransform: 'none', borderRadius: '9px' }}
            >
              Adicionar à lista
              {previaColados.validos.length ? ` (${previaColados.validos.length})` : ''}
            </Button>
            <Text component="span" className="font-light text-xs">
              Escreva <b>número, nome</b> para preencher a variável {'{{nome}}'}.
            </Text>
          </div>
        </div>
      )}

      {aviso && (
        <Text className="font-light text-xs mt-2" style={{ color: theme.palette.primary.main }}>
          {aviso}
        </Text>
      )}

      {/* A lista é sempre visível: sem isso o cliente não conferia quem estava
          salvo nem removia um número errado. */}
      <div className="mt-3 rounded-lg overflow-hidden" style={{ border: `1px solid ${theme.palette.divider}` }}>
        <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Text component="span" className="text-sm">
            <b style={{ color: '#25d366' }}>{lista.length}</b> destinatário
            {lista.length === 1 ? '' : 's'}
          </Text>
          <span style={{ flex: 1 }} />
          {lista.length > 0 && (
            <Button size="small" onClick={() => { onChange([]); setAviso('Lista esvaziada') }} sx={{ textTransform: 'none' }}>
              Limpar lista
            </Button>
          )}
        </div>

        {!lista.length ? (
          <Text className="font-light text-sm p-3">
            Nenhum destinatário ainda. Importe uma planilha ou cole os números acima.
          </Text>
        ) : (
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {lista.map((d) => (
              <div
                key={d.phone}
                className="flex items-center gap-2 px-3 py-1.5"
                style={{ borderBottom: `1px solid ${theme.palette.divider}22` }}
              >
                <Text component="span" className="text-sm" style={{ fontFamily: 'ui-monospace, monospace', minWidth: 130 }}>
                  {d.phone}
                </Text>
                <input
                  value={d.name || ''}
                  onChange={(e) => renomear(d.phone, e.target.value)}
                  placeholder="nome (opcional)"
                  className="text-sm px-2 py-1 rounded"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: 'transparent',
                    color: 'inherit',
                    border: `1px solid ${theme.palette.divider}`
                  }}
                />
                <Button size="small" onClick={() => remover(d.phone)} sx={{ textTransform: 'none', minWidth: 0 }}>
                  remover
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
